import { Component } from 'react'
import {
  TmrProduct,
  ProductionOrder,
  ProductionOrderRow,
  ProductionOrders,
  ProductionOrderRows,
  EncodingCounters,
  ProductInfoField
} from 'stylewhere/api'
import {
  Box,
  Text,
  FormSchemaForm,
  InfoBox,
  Spacer,
  EncodingProduct,
  EncodingCountersBox,
  KeyValueRow
} from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { AppStore, FormSchema, FormSchemaData } from 'stylewhere/shared'
import { Spinner } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { T, __ } from 'stylewhere/shared/i18n'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  getEncodingProductFields,
  getInitialType,
  showToastError,
  isProductionOrderCompleted,
  getInitialTypeOptions,
  showCountersOnEncoding
} from 'stylewhere/utils'

interface Props {
  operation: EncodingOperationConfig
  onSubmit: (data: FormSchemaData) => void
  onProductionOrderReceived?: (productionOrder: ProductionOrder) => void
  updateFormSchemaData?: (data: FormSchemaData) => void
  onWorkOrderClear?: () => void
  onProductClear?: () => void
  onIdentifierClear?: () => void
  data?: FormSchemaData
  product?: TmrProduct
  counters?: EncodingCounters
  resetStateCounter?: number
  disabledClear?: boolean
  disabledClearProduct?: boolean
  disableInput?: boolean
  overrideFormSchema?: FormSchema
  overrideProductInfoFields?: ProductInfoField[]
  style?: React.CSSProperties
  flex?: boolean
  productionOrderDetail?: () => void
}

interface State {
  data?: FormSchemaData
  productionOrder?: ProductionOrder
  productionOrderRow?: ProductionOrderRow
  resetStateCounter?: number
  loading: boolean
  loadingCounterProductionOrder: boolean
  loadingProductionOrderRow: boolean
}

export class EncodingForm extends Component<Props, State> {
  productionOrderFields = EncodingExtensions.getProductionOrderFields(this.props.operation)

  state: State = {
    data: this.props.data,
    resetStateCounter: this.props.resetStateCounter || 1,
    loading: false,
    loadingCounterProductionOrder: false,
    loadingProductionOrderRow: false
  }

  componentDidMount() {
    const { data } = this.props
    if (data && data.productionOrder && data.productionOrder.id) {
      this.setState({ productionOrder: data.productionOrder })
    }
  }

  shouldComponentUpdate = (nextProps) => {
    const { data, resetStateCounter, loading, loadingCounterProductionOrder, loadingProductionOrderRow } = this.state
    if (
      !loading &&
      !loadingCounterProductionOrder &&
      !loadingProductionOrderRow &&
      nextProps.data &&
      JSON.stringify(nextProps.data) !== JSON.stringify(data)
    ) {
      const initialType = getInitialType(this.props.operation)
      if (nextProps.resetStateCounter && resetStateCounter !== nextProps.resetStateCounter) {
        if (initialType === 'order') {
          this.setState({
            data: nextProps.data,
            resetStateCounter: nextProps.resetStateCounter,
            productionOrder: nextProps.data.productionOrder,
            productionOrderRow: undefined
          })
        } else {
          this.setState({
            data: nextProps.data,
            resetStateCounter: nextProps.resetStateCounter,
            productionOrderRow: undefined
          })
        }
      } else {
        const discard = initialType === 'order' && nextProps.data.productionOrderRow && !nextProps.data.productionOrder
        if (!discard) this.setState({ data: nextProps.data })
      }
    } else if (
      !loading &&
      !loadingCounterProductionOrder &&
      !loadingProductionOrderRow &&
      nextProps.resetStateCounter &&
      resetStateCounter !== nextProps.resetStateCounter
    ) {
      const initialType = getInitialType(this.props.operation)
      if (initialType === 'order') {
        this.setState({
          resetStateCounter: nextProps.resetStateCounter,
          productionOrder: undefined,
          productionOrderRow: undefined
        })
      } else {
        this.setState({ resetStateCounter: nextProps.resetStateCounter, productionOrderRow: undefined })
      }
    } else if (loadingProductionOrderRow && nextProps.data && nextProps.data.productionOrderRow) {
      this.setState({ loadingProductionOrderRow: false })
    }

    return true
  }

  initialTypeProduct = () => {
    const { data } = this.state
    const { disableInput, overrideFormSchema, style, flex, operation } = this.props
    return (
      <FormSchemaForm
        flex={flex === false ? flex : true}
        initialValues={data}
        style={style ?? { justifyContent: 'space-between' }}
        schema={overrideFormSchema ?? EncodingExtensions.formSchema(this.props.operation, data)}
        onSubmit={this.props.onSubmit}
        submitText={__(T.misc.next)}
        disabled={disableInput}
        operation={operation}
      />
    )
  }

  clearWorkOrderInfo = () => {
    this.props.onWorkOrderClear?.()
    if (this.state.data?.productionOrder)
      this.setState({
        productionOrderRow: undefined,
        data: { productionOrder: this.state.data.productionOrder }
      })
    else {
      this.setState({ productionOrderRow: undefined })
    }
  }

  clearProductInfo = () => {
    this.props.onProductClear?.()
    if (this.state.data?.productionOrder)
      this.setState({
        productionOrderRow: undefined,
        data: { productionOrder: this.state.data.productionOrder }
      })
    else {
      this.setState({ productionOrderRow: undefined })
    }
  }

  productInfoBox = () => {
    const {
      product,
      counters,
      onIdentifierClear,
      operation,
      disabledClear,
      disabledClearProduct,
      overrideProductInfoFields,
      flex,
      productionOrderDetail
    } = this.props
    const { productionOrder, productionOrderRow, data } = this.state
    return (
      <EncodingProduct
        initialType={getInitialType(operation)}
        code={data?.wam}
        operationId={operation.id}
        product={
          {
            ...(product ?? productionOrderRow!.product),
            productionOrderRow: productionOrderRow?.code
          } as any
        }
        productionOrder={productionOrder}
        clear={onIdentifierClear}
        productClose={disabledClearProduct ? undefined : this.clearProductInfo}
        fields={
          overrideProductInfoFields ??
          (productionOrderRow?.code
            ? [{ label: __(T.misc.production_order_row), path: 'productionOrderRow' }, ...getEncodingProductFields()]
            : getEncodingProductFields())
        }
        disabledClear={disabledClear}
        flex={flex}
        counters={counters}
        productionOrderDetail={productionOrderDetail}
      />
    )
  }

  formSchemaProductionOrder = () => {
    const { operation, overrideFormSchema, disableInput } = this.props
    return (
      <FormSchemaForm
        key="formProductionOrder"
        flex
        style={{ justifyContent: 'space-between' }}
        schema={overrideFormSchema ?? EncodingExtensions.formSchema(operation, this.state.data)}
        onSubmit={this.submitProductionOrder}
        submitText={__(T.misc.next)}
        disabled={disableInput}
        operation={operation}
      />
    )
  }

  renderLoading = () => {
    return (
      <KeyValueRow style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
        <Spinner thickness="5px" speed="0.65s" color="#454545" />
        <Text style={{ marginLeft: 10 }} center fontSize={14}>
          {__(T.misc.loading)}
        </Text>
      </KeyValueRow>
    )
  }

  formSchemaProductionOrderRow = () => {
    const initialType = getInitialType(this.props.operation)
    const { disableInput, operation, overrideFormSchema, counters, productionOrderDetail } = this.props
    const { productionOrder, productionOrderRow, loadingCounterProductionOrder, loadingProductionOrderRow } = this.state
    return (
      <Box flex>
        {initialType !== 'orderRow' && productionOrder && (
          <>
            <InfoBox
              title={__(T.misc.work_order)}
              subtitle={productionOrder?.code}
              data={productionOrder}
              fields={this.productionOrderFields}
              onCloseClick={this.clearWorkOrderInfo}
            />
            <Spacer height={'10'} />
            {!productionOrderRow && loadingCounterProductionOrder && (
              <>
                {this.renderLoading()}
                <Spacer height={'10'} />
              </>
            )}
            {(!productionOrderRow || loadingProductionOrderRow) &&
              counters &&
              showCountersOnEncoding(initialType, counters) &&
              !loadingCounterProductionOrder && (
                <>
                  <EncodingCountersBox
                    counters={counters}
                    productionOrder={productionOrder}
                    productionOrderDetail={productionOrderDetail}
                  />
                  <Spacer height={'10'} />
                </>
              )}
          </>
        )}
        {!!productionOrderRow && !loadingProductionOrderRow && this.productInfoBox()}
        {loadingProductionOrderRow && this.renderLoading()}
        {!productionOrderRow && !loadingProductionOrderRow && !isProductionOrderCompleted(counters) && (
          <FormSchemaForm
            key="formProductionOrderRow"
            flex
            style={{ justifyContent: 'space-between' }}
            schema={overrideFormSchema ?? EncodingExtensions.formSchema(operation, this.state.data)}
            onSubmit={this.submitProductionOrderRow}
            // initialValues={this.state.data}
            submitText={__(T.misc.next)}
            disabled={disableInput}
            operation={operation}
          />
        )}
      </Box>
    )
  }

  submitProductionOrder = async (data: { productionOrder: string }) => {
    const { onProductionOrderReceived, updateFormSchemaData } = this.props
    try {
      if (data.productionOrder) {
        const initialTypeOptions = getInitialTypeOptions(this.props.operation)
        const additionaParameters = initialTypeOptions?.productionOrdersParams || {}
        const res = await ProductionOrders.search<ProductionOrder>({
          equalCodes: data.productionOrder,
          ...additionaParameters
        })
        if (res.content.length === 0) throw new Error('Production order not found')
        const productionOrder = res.content[0]
        if (this.checkWorkerOnProductionOrder() === 'yes') {
          const workstationPlaceCode = AppStore.getWorkstationPlaceCode()
          if (
            productionOrder.worker.code !== workstationPlaceCode &&
            !AppStore.hasAnyCapabilities('System.User.allPlaceAccess')
          ) {
            showToastError(__(T.error.WRONG_PLACE))
            return
          }
        } else if (this.checkWorkerOnProductionOrder() === 'userPlaces') {
          const user = AppStore.loggedUser
          const userPlaces = user?.userPlaces
          if (userPlaces && userPlaces.length > 0) {
            const workerCode = productionOrder.worker.code
            const userPlaceCodes = userPlaces.map((place) => place.code)
            const hasAccess = userPlaceCodes.includes(workerCode)

            if (!hasAccess && !AppStore.hasAnyCapabilities('System.User.allPlaceAccess')) {
              showToastError(__(T.error.WRONG_PLACE))
              return
            }
          }
        }
        this.setState({ data, productionOrder })
        if (onProductionOrderReceived) {
          this.setState({ loadingCounterProductionOrder: true })
          await onProductionOrderReceived(productionOrder)
          this.setState({ loadingCounterProductionOrder: false })
        }
        if (updateFormSchemaData) {
          updateFormSchemaData({ productionOrder })
        }
      }
    } catch (error) {
      showToastError(__(T.error.couldn_find_production_order))
    }
  }

  submitProductionOrderRow = async (data: { productionOrder: string; productionOrderRow: string }) => {
    this.setState(
      {
        loadingProductionOrderRow: true,
        data: {
          ...this.state.data,
          productionOrderRow: data.productionOrderRow
        }
      },
      this.processProductionOrderRow
    )
  }

  processProductionOrderRow = async () => {
    const { productionOrder, data } = this.state
    const { operation, onSubmit } = this.props
    try {
      let productionOrderRow
      const mode = this.getOrderSelectionMode()
      const initialType = getInitialType(operation)
      const productionOrderCodes = initialType !== 'orderRow' ? [data.productionOrder.code ?? data.productionOrder] : []
      const param =
        mode === 'byOrderAndSku'
          ? {
              equalProductionOrderCodes: productionOrderCodes,
              equalProductCodes: data.productionOrderRow
            }
          : {
              equalProductionOrderCodes: productionOrderCodes,
              equalCodes: data.productionOrderRow
            }
      const res = await ProductionOrderRows.search<ProductionOrderRow>(param)
      if (res && res.content && res.content.length > 0) {
        productionOrderRow = res.content[0]
        this.setState({ productionOrderRow })

        if (initialType !== 'orderRow') onSubmit({ productionOrder, productionOrderRow })
        else onSubmit({ productionOrderRow })
      } else {
        this.setState({ loadingProductionOrderRow: false })
        throw new Error('Production order row not found')
      }
    } catch (error) {
      this.setState({ loadingProductionOrderRow: false })
      showToastError(__(T.error.couldn_find_production_order_row))
    }
  }

  getOrderSelectionMode = () => {
    const { operation } = this.props
    return operation.options?.productionOrderRowSelectionMode
      ? operation.options.productionOrderRowSelectionMode
      : 'byOrderAndRowCode'
  }

  checkWorkerOnProductionOrder = () => {
    const { operation } = this.props
    return operation?.options?.checkWorkerOnProductionOrder
  }

  render() {
    const initialType = getInitialType(this.props.operation)
    const { data, loading } = this.state
    const { product } = this.props

    if (loading) {
      return (
        <LoadingContainer>
          <Spinner thickness="5px" speed="0.65s" color="#454545" />
          <Text style={{ marginTop: 15 }} center fontSize={14}>
            {__(T.misc.loading)}
          </Text>
        </LoadingContainer>
      )
    }

    if (initialType === 'product' || initialType === 'wam' || initialType === 'formSchema') {
      if (product) return this.productInfoBox()
      return this.initialTypeProduct()
    }

    // Initial type is production order
    if (initialType === 'order') {
      if (!data || !data.productionOrder) return this.formSchemaProductionOrder()
      else if (data && data.productionOrder) {
        return this.formSchemaProductionOrderRow()
      }
    }

    // Initial type is order row
    if (initialType === 'orderRow') return this.formSchemaProductionOrderRow()

    return null
  }
}

const LoadingContainer = styled(Box)`
  background: #ffffff;
  box-sizing: border-box;
  border-radius: 15px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  flex: 1;
`
